import React, { Component } from "react";
import {
  company,
  loading,
  Modal,
  Select,
  DatePicker,
  alert,
  POST,
  PUT,
  DELETE,
  t,
  activeOptions,
} from "./CustomComponent.js";

/**
 * Modal เพิ่ม/แก้ไข/ลบ "กิจกรรมช่าง" (admin/activities) ใช้ร่วมกันระหว่างหน้าตั้งค่าปฏิทิน (holidays.js)
 * และหน้าจัดตารางงาน (scheduling.js)
 * props:
 * - show: เปิด/ปิด modal
 * - activity: กิจกรรมที่จะแก้ไข (object จาก admin/activities) - ไม่ส่ง/null = โหมดเพิ่มใหม่
 * - defaultDate, defaultTechnicianUid: ค่าเริ่มต้นตอนเพิ่มใหม่
 * - technicians: รายชื่อช่างดิบจาก admin/technicians, objectives: วัตถุประสงค์ (usage_type technician)
 * - onClose(): ปิด modal, onSaved(): เรียกหลังบันทึก/ลบสำเร็จ (ให้หน้าแม่โหลดข้อมูลใหม่)
 * - alertUrl: url ที่ส่งให้ alert() ตอนบันทึก/ลบสำเร็จ ("#" = รีโหลดหน้า ซึ่งเป็นพฤติกรรมเดิมของ holidays.js, "" = ไม่รีโหลด)
 */

// สิทธิ์จาก profile ที่แคชไว้ (รูปแบบเดียวกับ NavbarAdmin.permissionSetting) - ไม่พึ่งการลบ element ตาม class
// เพราะ modal นี้ mount แยกจาก navbar จึงไม่ถูก permissionSetting() กวาดตอนเปิด
const hasPermission = (perm) => {
  try {
    const profile = JSON.parse(localStorage.getItem("cm-profile") || "null");
    return new Set(profile?.role?.permission || []).has(perm);
  } catch (e) {
    return false;
  }
};

const currentLang = () => ((localStorage.getItem("lang") || "TH") === "EN" ? "en" : "th");

export default class ActivityModal extends Component {
  constructor(props) {
    super(props);
    this.state = this.buildInitialState(props);
  }

  buildInitialState = (props) => {
    const activity = props.activity;
    if (activity) {
      return {
        activity_uid: activity.uid,
        activity_technician_uids: (activity.technicians || []).map((tc) => tc.uid),
        // ช่างที่ถูกเลือกไว้ตั้งแต่เปิด modal — ยังโชว์ในรายการแม้ปิดใช้งานแล้ว (ติ๊กออกแล้วติ๊กกลับได้)
        activity_initial_technician_uids: (activity.technicians || []).map((tc) => tc.uid),
        activity_start_date: activity.start_date || "",
        activity_end_date: activity.end_date || "",
        activity_time_start: activity.time_start || "",
        activity_time_end: activity.time_end || "",
        activity_objective_id: activity.objective?.uid || "",
        activity_detail: activity.detail || "",
        activity_tech_picker_open: false,
        activity_tech_search: "",
      };
    }
    return {
      activity_uid: "",
      activity_technician_uids: props.defaultTechnicianUid ? [props.defaultTechnicianUid] : [],
      activity_initial_technician_uids: props.defaultTechnicianUid ? [props.defaultTechnicianUid] : [],
      activity_start_date: props.defaultDate || "",
      activity_end_date: props.defaultDate || "",
      activity_time_start: "",
      activity_time_end: "",
      activity_objective_id: "",
      activity_detail: "",
      activity_tech_picker_open: false,
      activity_tech_search: "",
    };
  };

  // เปิด modal ครั้งใหม่ = โหลดค่าเริ่มต้นใหม่ทั้งชุด
  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) this.setState(this.buildInitialState(this.props));
  }

  close = () => this.props.onClose && this.props.onClose();

  // ชื่อช่างรูปแบบเดียวกับช่อง "ช่างที่โทร" ใน Fast Call: ชื่อ นามสกุล (ชื่อเล่น) ไทยก่อน ไม่มีค่อยใช้อังกฤษ
  getTechnicianDisplayName = (technician) => {
    const nicknameTh = technician?.nickname_th ? ` (${technician.nickname_th})` : "";
    const nicknameEn = technician?.nickname_en ? ` (${technician.nickname_en})` : "";
    return (
      [technician?.first_name_th, technician?.last_name_th, nicknameTh].filter(Boolean).join(" ") ||
      [technician?.first_name_en, technician?.last_name_en, nicknameEn].filter(Boolean).join(" ") ||
      "-"
    );
  };

  toggleActivityTechnician = (uid) => {
    const list = this.state.activity_technician_uids;
    this.setState({ activity_technician_uids: list.includes(uid) ? list.filter((v) => v !== uid) : [...list, uid] });
  };

  // เวลาส่งไป backend ได้เฉพาะกรณีวันเดียว (start_date === end_date) - ใช้ตอนประกอบ payload ตอนบันทึกเท่านั้น
  activityIsSameDay = () => {
    const { activity_start_date, activity_end_date } = this.state;
    return !!activity_start_date && !!activity_end_date && activity_start_date === activity_end_date;
  };

  // ล็อกช่องเวลาเฉพาะตอนเลือกวันที่ครบทั้งสองช่องแล้ว "ต่างวันกันจริง" เท่านั้น
  // ยังไม่เลือกวันที่สิ้นสุด หรือเลือกวันเดียวกัน = ยังกรอกเวลาได้ปกติ
  activityTimeLocked = () => {
    const { activity_start_date, activity_end_date } = this.state;
    return !!activity_start_date && !!activity_end_date && activity_start_date !== activity_end_date;
  };

  CreateUpdateActivity = async () => {
    const {
      activity_uid, activity_technician_uids, activity_start_date, activity_end_date,
      activity_time_start, activity_time_end, activity_objective_id, activity_detail,
    } = this.state;

    if (activity_technician_uids.length === 0) {
      alert("", "warning", t("warning"), t("act_verify_technician"));
      return;
    }
    if (!activity_start_date || !activity_end_date) {
      alert("", "warning", t("warning"), t("act_verify_date"));
      return;
    }
    if (activity_end_date < activity_start_date) {
      alert("", "warning", t("warning"), t("hd_verify_end_date"));
      return;
    }
    if (!activity_objective_id) {
      alert("", "warning", t("warning"), t("act_verify_objective"));
      return;
    }

    const alertUrl = this.props.alertUrl === undefined ? "#" : this.props.alertUrl;
    if (await alert("", "info", t("confirm"), t("confirm_submission"), t("confirm"), t("cancel"))) {
      loading(true);
      const sameDay = this.activityIsSameDay();
      const base = {
        technician_uids: activity_technician_uids,
        objective_id: activity_objective_id,
        start_date: activity_start_date,
        end_date: activity_end_date,
        time_start: sameDay ? (activity_time_start || null) : null,
        time_end: sameDay ? (activity_time_end || null) : null,
        detail: activity_detail.trim(),
      };

      const result = !activity_uid
        ? await POST("admin/activities", { ...base, company_id: company?.id || "" })
        : await PUT("admin/activities/" + activity_uid, base);

      if (result?.status === true) {
        alert(alertUrl, "success", t("success"), t("save_success"));
        this.close();
        if (this.props.onSaved) this.props.onSaved();
      } else {
        alert("", "warning", t("warning"), result?.message || "An error occurred.");
      }
      loading(false);
    }
  };

  DeleteActivity = async () => {
    const { activity_uid } = this.state;
    const alertUrl = this.props.alertUrl === undefined ? "#" : this.props.alertUrl;
    if (await alert("", "warning", t("delete_item"), t("delete_desc"), t("confirm"), t("cancel"))) {
      loading(true);
      const result = await DELETE("admin/activities/" + activity_uid, null);
      if (result?.status) {
        alert(alertUrl, "success", t("success"), t("delete_success"));
        this.close();
        if (this.props.onSaved) this.props.onSaved();
      } else {
        alert("", "warning", t("warning"), result?.message || "An error occurred.");
      }
      loading(false);
    }
  };

  render() {
    const { show, technicians: techProp, objectives: objProp } = this.props;
    const {
      activity_uid,
      activity_technician_uids, activity_initial_technician_uids, activity_start_date, activity_end_date,
      activity_time_start, activity_time_end, activity_objective_id, activity_detail,
      activity_tech_picker_open, activity_tech_search,
    } = this.state;
    const isEdit = !!activity_uid;
    const technicians = techProp || [];
    const objectives = objProp || [];
    const timeLocked = this.activityTimeLocked();
    const selectableTechnicians = technicians.filter(
      (tc) =>
        tc.is_active !== false ||
        activity_technician_uids.includes(tc.uid) ||
        (activity_initial_technician_uids || []).includes(tc.uid),
    );
    const filteredTechnicians = selectableTechnicians.filter(
      (tc) => !activity_tech_search || this.getTechnicianDisplayName(tc).toLowerCase().includes(activity_tech_search.toLowerCase()),
    );

    return (
      <Modal
        show={show}
        onHide={this.close}
        centered
        contentClassName="border-0 rounded-4 shadow-lg"
      >
        <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton>
          <h5 className="fw-bold mb-0 text-dark">{isEdit ? t("act_edit") : t("act_add")}</h5>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          <div className="row g-3">
            <div className="col-12 position-relative">
              <label className="form-label text-14 text-dark mb-2">{t("technicians")} <span className="text-danger">*</span></label>
              <div
                className={`border rounded-3 p-2 d-flex align-items-center bg-white ${activity_tech_picker_open ? "border-primary" : ""}`}
                style={{ minHeight: "48px", cursor: "pointer" }}
                onClick={() => this.setState({ activity_tech_picker_open: !activity_tech_picker_open })}
              >
                <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
                  {activity_technician_uids.length === 0 && <span className="text-secondary text-12 px-2">{t("select_technician")}</span>}
                  {activity_technician_uids.map((tuid) => {
                    const tc = (technicians || []).find((v) => v.uid === tuid);
                    return (
                      <span key={tuid} className="badge bg-primary-light text-primary border border-primary px-2 py-2 fw-medium text-12 d-inline-flex align-items-center">
                        {tc ? this.getTechnicianDisplayName(tc) : tuid}
                        <span
                          className="icon ms-2 text-12 cursor-pointer text-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.toggleActivityTechnician(tuid);
                          }}
                        >
                          {""}
                        </span>
                      </span>
                    );
                  })}
                </div>
                <span className="icon text-secondary ms-2">{activity_tech_picker_open ? "" : ""}</span>
              </div>

              {activity_tech_picker_open && (
                <>
                  <div className="position-fixed" style={{ inset: 0, zIndex: 1040 }} onClick={() => this.setState({ activity_tech_picker_open: false, activity_tech_search: "" })} />
                  <div className="position-absolute bg-white border rounded-3 shadow w-100 mt-1 p-2" style={{ zIndex: 1050, maxHeight: "300px", overflowY: "auto" }}>
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder={t("search")}
                      value={activity_tech_search}
                      onChange={(e) => this.setState({ activity_tech_search: e.target.value })}
                    />
                    <label className="d-flex align-items-center gap-2 px-2 py-2 border-bottom fw-bold text-dark mb-1" style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={selectableTechnicians.length > 0 && selectableTechnicians.every((tc) => activity_technician_uids.includes(tc.uid))}
                        onChange={() => {
                          const all = selectableTechnicians.map((tc) => tc.uid);
                          const hasAll = all.every((tuid) => activity_technician_uids.includes(tuid));
                          this.setState({ activity_technician_uids: hasAll ? [] : all });
                        }}
                      />
                      {t("select_all")}
                    </label>
                    {filteredTechnicians.map((tc) => (
                      <label key={tc.uid} className="d-flex align-items-center gap-2 px-2 py-2 rounded-2 hover-bg-light" style={{ cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={activity_technician_uids.includes(tc.uid)}
                          onChange={() => this.toggleActivityTechnician(tc.uid)}
                        />
                        <span className="text-14 text-dark">{this.getTechnicianDisplayName(tc)}</span>
                      </label>
                    ))}
                    {filteredTechnicians.length === 0 && <div className="text-center text-secondary text-12 py-3">{t("no_data")}</div>}
                  </div>
                </>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label text-14 text-dark mb-2">{t("start_date")} <span className="text-danger">*</span></label>
              <DatePicker
                type={currentLang()}
                className="form-control"
                value={activity_start_date}
                onChange={(value) => {
                  // ถ้าวันที่สิ้นสุดเดิมมาก่อนวันที่เริ่มใหม่ ให้ขยับตามไปด้วย (กันช่วงวันที่กลับด้าน)
                  const nextEnd = activity_end_date && activity_end_date >= value ? activity_end_date : value;
                  this.setState({
                    activity_start_date: value,
                    activity_end_date: nextEnd,
                    ...(value !== nextEnd ? { activity_time_start: "", activity_time_end: "" } : {}),
                  });
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label text-14 text-dark mb-2">{t("end_date")} <span className="text-danger">*</span></label>
              <DatePicker
                type={currentLang()}
                className="form-control"
                value={activity_end_date}
                min={activity_start_date || undefined}
                onChange={(value) => this.setState({
                  activity_end_date: value,
                  // เคลียร์เวลาเฉพาะตอนกลายเป็นช่วงหลายวันจริง (มีวันเริ่มแล้วและไม่ตรงกับวันสิ้นสุดใหม่)
                  ...(activity_start_date && activity_start_date !== value ? { activity_time_start: "", activity_time_end: "" } : {}),
                })}
              />
            </div>

            <div className="col-md-6">
              <label className={`form-label text-14 mb-2 ${timeLocked ? "text-secondary" : "text-dark"}`}>{t("act_time_start")}</label>
              <input
                type="time"
                className="form-control py-2"
                disabled={timeLocked}
                value={timeLocked ? "" : activity_time_start}
                onChange={(e) => this.setState({ activity_time_start: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className={`form-label text-14 mb-2 ${timeLocked ? "text-secondary" : "text-dark"}`}>{t("act_time_end")}</label>
              <input
                type="time"
                className="form-control py-2"
                disabled={timeLocked}
                min={activity_time_start || undefined}
                value={timeLocked ? "" : activity_time_end}
                onChange={(e) => this.setState({ activity_time_end: e.target.value })}
              />
            </div>
            <div className="col-12">
              <div className="text-secondary text-12">{timeLocked ? t("act_time_locked_hint") : t("act_time_free_hint")}</div>
            </div>

            <div className="col-12">
              <label className="form-label text-14 text-dark mb-2">{t("act_objective")} <span className="text-danger">*</span></label>
              <Select
                className="form-control"
                placeholder={t("act_objective_ph")}
                options={activeOptions((objectives || []).map((o) => ({ value: o.uid, label: o.name, is_active: o.is_active })), activity_objective_id)}
                value={activity_objective_id}
                onChange={(value) => this.setState({ activity_objective_id: value })}
              />
            </div>
            <div className="col-12">
              <label className="form-label text-14 text-dark mb-2">{t("act_detail")}</label>
              <textarea
                className="form-control py-2"
                rows={2}
                placeholder={t("act_detail_ph")}
                value={activity_detail}
                onChange={(e) => this.setState({ activity_detail: e.target.value })}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-between gap-2">
          <div>
            {isEdit && hasPermission("activity.delete") && (
              <button className="btn btn-white border border-danger text-danger px-4 py-2 rounded-3 fw-bold activity.delete" onClick={this.DeleteActivity}>
                <span className="icon me-2">{""}</span> {t("delete")}
              </button>
            )}
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold" onClick={this.close}>{t("cancel")}</button>
            <button className="btn px-4 py-2 d-flex align-items-center rounded-3 fw-bold shadow-sm hd-bg-plan-solid" onClick={this.CreateUpdateActivity}>
              <span className="icon me-2">{""}</span> {t("save")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}
